import React, { useState, useCallback } from 'react';
import { VoiceRecorder } from './VoiceRecorder';
import { PlayIcon, StopIcon, DeleteIcon } from './CustomIcons';

interface VoiceRecording {
  id: string;
  transcription: string;
  audioBlob: Blob;
  mimeType: string;
  timestamp: number;
}

interface VoiceTextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
  voiceRecording?: VoiceRecording | null;
  onVoiceRecordingReceived?: (result: { transcription: string; audioBlob: Blob; mimeType: string }) => void;
  onVoiceError?: (error: string) => void;
  onClearVoiceRecording?: () => void;
}

export const VoiceTextInput: React.FC<VoiceTextInputProps> = ({
  value,
  onChange,
  placeholder = "Type your explanation or use voice input...",
  disabled = false,
  rows = 4,
  className = "",
  voiceRecording = null,
  onVoiceRecordingReceived,
  onVoiceError,
  onClearVoiceRecording
}) => {
  const [voiceError, setVoiceError] = useState<string>('');
  const [playingAudio, setPlayingAudio] = useState<boolean>(false);
  const [currentAudioSource, setCurrentAudioSource] = useState<AudioBufferSourceNode | null>(null);

  const handleTranscriptionReceived = useCallback((result: { transcription: string; audioBlob: Blob; mimeType: string }) => {
    if (onVoiceRecordingReceived) {
      onVoiceRecordingReceived(result);
    }
    setVoiceError('');
  }, [onVoiceRecordingReceived]);

  const handleVoiceError = useCallback((error: string) => {
    setVoiceError(error);
    if (onVoiceError) {
      onVoiceError(error);
    }
  }, [onVoiceError]);

  // Audio playback functionality for single recording
  const toggleAudioPlayback = useCallback(async () => {
    if (!voiceRecording || voiceRecording.audioBlob.size === 0) {
      alert('No audio available for playback');
      return;
    }

    // If currently playing, stop it
    if (playingAudio && currentAudioSource) {
      currentAudioSource.stop();
      setCurrentAudioSource(null);
      setPlayingAudio(false);
      return;
    }

    // Start new playback using Web Audio API
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) {
        alert('Web Audio API not supported in this browser');
        return;
      }

      const audioContext = new AudioContext();
      const arrayBuffer = await voiceRecording.audioBlob.arrayBuffer();
      
      audioContext.decodeAudioData(arrayBuffer).then(audioBuffer => {
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        
        setPlayingAudio(true);
        setCurrentAudioSource(source);
        
        source.onended = () => {
          setPlayingAudio(false);
          setCurrentAudioSource(null);
        };
        
        source.start(0);
        console.log('Playing voice recording via Web Audio API');
        
      }).catch(error => {
        console.error('Web Audio API decode failed:', error);
        setPlayingAudio(false);
        setCurrentAudioSource(null);
        alert('Audio playback failed - format may not be supported.');
      });
      
    } catch (error) {
      console.error('Error in audio playback:', error);
      setPlayingAudio(false);
      setCurrentAudioSource(null);
    }
  }, [voiceRecording, playingAudio, currentAudioSource]);

  const clearRecording = () => {
    // Stop any playing audio first
    if (currentAudioSource) {
      currentAudioSource.stop();
      setCurrentAudioSource(null);
      setPlayingAudio(false);
    }
    if (onClearVoiceRecording) {
      onClearVoiceRecording();
    }
    setVoiceError('');
  };

  return (
    <div className={`voice-text-input ${className}`}>
      <div className="input-header">
        <div className="input-controls">
          <VoiceRecorder
            onTranscriptionReceived={handleTranscriptionReceived}
            onError={handleVoiceError}
            disabled={disabled}
          />
          
          {voiceRecording && (
            <>
              <button
                type="button"
                className="voice-play-btn"
                onClick={toggleAudioPlayback}
                disabled={disabled || voiceRecording.audioBlob.size === 0}
                title={playingAudio ? "Stop playback" : "Play recording"}
              >
                {playingAudio ? <StopIcon size={16} /> : <PlayIcon size={16} />}
              </button>
              
              <button
                type="button"
                className="clear-transcriptions-btn"
                onClick={clearRecording}
                disabled={disabled}
                title="Clear voice recording"
              >
                <DeleteIcon size={16} />
                <span>Clear Voice</span>
              </button>
            </>
          )}
        </div>
        
        {voiceRecording && (
          <div className="transcription-count">
            Voice recording available
          </div>
        )}
      </div>

      <textarea
        className="text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
      />

      {voiceError && (
        <div className="voice-error">
          <span className="error-icon">⚠️</span> {voiceError}
        </div>
      )}

      {voiceRecording && (
        <div className="transcriptions-preview">
          <div className="transcriptions-header">
            <strong>Voice Recording:</strong>
          </div>
          <div className="transcription-item">
            <span className="transcription-text">{voiceRecording.transcription}</span>
          </div>
        </div>
      )}
    </div>
  );
};